import React, { useState, useEffect } from 'react';
import { CloseIcon } from '../Icons/Icons';
import './LoanCalculator.css';

function LoanCalculator({ onClose }) {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(12);
  const [loanTerm, setLoanTerm] = useState(20);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  useEffect(() => {
    calculateLoan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanAmount, interestRate, loanTerm]);

  const calculateLoan = () => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate) / 100 / 12;
    const months = parseFloat(loanTerm) * 12;

    if (principal > 0 && rate > 0 && months > 0) {
      // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
      const x = Math.pow(1 + rate, months);
      const monthly = (principal * rate * x) / (x - 1);

      const total = monthly * months;
      const interest = total - principal;

      setMonthlyPayment(monthly);
      setTotalPayment(total);
      setTotalInterest(interest);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="calculator-modal-overlay" onClick={onClose}>
      <div className="calculator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="calculator-header">
          <h2>Home Loan Calculator</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close calculator">
            <CloseIcon size={24} color="#636e72" />
          </button>
        </div>

        <div className="calculator-body">
          <div className="calculator-inputs">
            <div className="input-group">
              <label htmlFor="loan-amount">
                Loan Amount
                <span className="input-value">{formatCurrency(loanAmount)}</span>
              </label>
              <input
                id="loan-amount"
                type="range"
                min="500000"
                max="50000000"
                step="100000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                className="range-slider"
              />
              <div className="range-labels">
                <span>PKR 5 Lac</span>
                <span>PKR 5 Crore</span>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="interest-rate">
                Interest Rate
                <span className="input-value">{interestRate}% per year</span>
              </label>
              <input
                id="interest-rate"
                type="range"
                min="5"
                max="25"
                step="0.5"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="range-slider"
              />
              <div className="range-labels">
                <span>5%</span>
                <span>25%</span>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="loan-term">
                Loan Term
                <span className="input-value">{loanTerm} years</span>
              </label>
              <input
                id="loan-term"
                type="range"
                min="1"
                max="30"
                step="1"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                className="range-slider"
              />
              <div className="range-labels">
                <span>1 year</span>
                <span>30 years</span>
              </div>
            </div>
          </div>

          <div className="calculator-results">
            <div className="result-card result-primary">
              <div className="result-label">Monthly Payment</div>
              <div className="result-value">{formatCurrency(monthlyPayment)}</div>
            </div>

            <div className="result-card">
              <div className="result-label">Total Payment</div>
              <div className="result-value">{formatCurrency(totalPayment)}</div>
            </div>

            <div className="result-card">
              <div className="result-label">Total Interest</div>
              <div className="result-value">{formatCurrency(totalInterest)}</div>
            </div>
          </div>

          <div className="calculator-breakdown">
            <h3>Payment Breakdown</h3>
            <div className="breakdown-chart">
              <div
                className="breakdown-bar principal"
                style={{ width: `${(loanAmount / totalPayment) * 100}%` }}
              >
                <span>Principal</span>
              </div>
              <div
                className="breakdown-bar interest"
                style={{ width: `${(totalInterest / totalPayment) * 100}%` }}
              >
                <span>Interest</span>
              </div>
            </div>
            <div className="breakdown-legend">
              <div className="legend-item">
                <span className="legend-color principal"></span>
                <span>Principal: {formatCurrency(loanAmount)}</span>
              </div>
              <div className="legend-item">
                <span className="legend-color interest"></span>
                <span>Interest: {formatCurrency(totalInterest)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoanCalculator;
